/** The invite sheet: share, QR code for people in the room, and the raw link. */

import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { inviteUrl } from '../codes.js';
import { shareInvite, smsLink, whatsappLink, type ShareOutcome } from '../share.js';
import { useT } from '../i18n.js';

export function InviteSheet({ code, origin, onClose }: {
  code: string; origin: string; onClose: () => void;
}) {
  const t = useT();
  const url = inviteUrl(code, origin);
  const [qr, setQr] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<ShareOutcome | null>(null);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url, { width: 320, margin: 1, color: { dark: '#16242b', light: '#f6f1e4' } })
      .then((data) => { if (!cancelled) setQr(data); })
      .catch(() => { if (!cancelled) setQr(null); });
    return () => { cancelled = true; };
  }, [url]);

  async function onShare() {
    setOutcome(await shareInvite(code, url, {}, t));
  }

  return (
    <div
      role="dialog"
      aria-label={t('invite.title')}
      data-testid="invite-sheet"
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/70"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-t-3xl bg-felt p-5 pb-[calc(1.25rem+var(--pad-bottom))]"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="mb-1 text-center text-xl font-black text-sun">{t('invite.title')}</h2>
        <p className="mb-4 text-center text-sm text-bone/60">
          {t('invite.subtitle')}
        </p>

        {qr && (
          <img
            src={qr}
            alt={t('invite.qrAlt', { code })}
            data-testid="invite-qr"
            className="mx-auto mb-4 h-44 w-44 rounded-xl"
          />
        )}

        <button
          type="button"
          data-testid="share-native"
          onClick={onShare}
          className="mb-2 w-full rounded-2xl bg-sun py-4 text-lg font-black text-ink active:scale-95"
        >
          {t('invite.share')}
        </button>

        <div className="mb-3 flex gap-2">
          <a
            href={whatsappLink(code, url, t)}
            data-testid="share-whatsapp"
            target="_blank"
            rel="noreferrer"
            className="flex-1 rounded-xl bg-white/10 py-3 text-center font-bold"
          >
            {t('invite.whatsapp')}
          </a>
          <a
            href={smsLink(code, url, t)}
            data-testid="share-sms"
            className="flex-1 rounded-xl bg-white/10 py-3 text-center font-bold"
          >
            {t('invite.text')}
          </a>
        </div>

        <p data-testid="invite-url" className="mb-3 truncate text-center text-xs text-bone/50">
          {url}
        </p>

        {outcome && (
          <p data-testid="share-outcome" aria-live="polite" className="mb-3 text-center text-sm text-sun">
            {t(`invite.${outcome}`)}
          </p>
        )}

        <button
          type="button"
          data-testid="invite-close"
          onClick={onClose}
          className="w-full rounded-2xl bg-white/10 py-3 font-bold"
        >
          {t('common.done')}
        </button>
      </div>
    </div>
  );
}
